import { useState } from 'react';
import type { ServiceSuggestion, ServiceDefinition } from '@om/shared-types';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ServiceCheckboxItem } from './ServiceCheckboxItem';
import { ServiceDiscoverySearch } from './ServiceDiscoverySearch';

interface SelectionState {
  id: number;
  selected: boolean;
  priority: 'must_have' | 'optional';
}

interface CategoryAccordionProps {
  category: string;
  services: ServiceSuggestion[];
  defaultOpen?: boolean;
  onSelectionChange: (category: string, selections: SelectionState[]) => void;
}

export function CategoryAccordion({
  category,
  services: initialServices,
  defaultOpen = true,
  onSelectionChange,
}: CategoryAccordionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [services, setServices] = useState<ServiceSuggestion[]>(initialServices);
  const [selections, setSelections] = useState<SelectionState[]>(
    initialServices.map((s) => ({
      id: s.id,
      selected: s.is_selected,
      priority: s.priority ?? 'optional',
    }))
  );

  const selectedCount = selections.filter((s) => s.selected).length;

  function handleItemChange(id: number, selected: boolean, priority: 'must_have' | 'optional') {
    const updated = selections.map((s) =>
      s.id === id ? { ...s, selected, priority } : s
    );
    setSelections(updated);
    onSelectionChange(category, updated);
  }

  function handleAddService(def: ServiceDefinition) {
    // Prevent duplicates
    if (services.some((s) => s.id === def.id)) return;

    const newSuggestion: ServiceSuggestion = {
      ...def,
      suggestion_reason: 'Manually added',
      is_selected: true,
      priority: 'optional',
    };
    const updatedServices = [...services, newSuggestion];
    const updatedSelections: SelectionState[] = [
      ...selections,
      { id: def.id, selected: true, priority: 'optional' },
    ];
    setServices(updatedServices);
    setSelections(updatedSelections);
    onSelectionChange(category, updatedSelections);
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <span className="font-medium text-gray-900">{category}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
            {services.length} services
          </span>
        </div>
        {selectedCount > 0 && (
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            {selectedCount} selected
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-2">
          <div className="divide-y divide-gray-50">
            {services.map((svc) => (
              <ServiceCheckboxItem
                key={svc.id}
                service={{
                  ...svc,
                  is_selected: selections.find((s) => s.id === svc.id)?.selected ?? svc.is_selected,
                  priority: selections.find((s) => s.id === svc.id)?.priority ?? svc.priority,
                }}
                onChange={handleItemChange}
              />
            ))}
          </div>
          <ServiceDiscoverySearch category={category} onAdd={handleAddService} />
        </div>
      )}
    </div>
  );
}
